import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Eye, ArrowUpRight } from 'lucide-react';
import PrivateBookingBadge from '../../shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../../utils/bookingSource';
import { paymentCatalogAmount } from '../../../utils/formatMoney';

const RecentPayments = ({ payments = [] }) => {
 const navigate = useNavigate();
 const visiblePayments = payments.filter((payment) => payment.status !== 'pending');

 const formatCurrency = (amount) => {
 if (amount === null || amount === undefined || amount === '') return '$0.00';
 const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
 if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
 return `$${numAmount.toLocaleString('en-US', {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
 })}`;
 };

 const formatDate = (dateString) => {
 if (!dateString) return 'N/A';
 return new Date(dateString).toLocaleDateString('en-US', {
 month: 'short',
 day: 'numeric',
 year: 'numeric',
 });
 };

 const getStatusColor = (status) => {
 switch (status) {
 case 'completed':
 return 'bg-emerald-100 text-emerald-800';
 case 'failed':
 return 'bg-red-100 text-red-800';
 default:
 return 'bg-gray-100 text-gray-800';
 }
 };

 const openPayment = (payment) => {
 const bookingId = payment.booking_id || payment.booking?.id;
 if (bookingId) {
 navigate(`/admin/bookings/${bookingId}`);
 return;
 }
 navigate('/admin/payments');
 };

 return (
 <div className="product-panel overflow-hidden">
 <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 sm:pt-5 pb-3">
 <div>
 <h2 className="text-base font-semibold text-ink">Recent payments</h2>
 <p className="text-xs text-ink-tertiary mt-0.5">Settled client charges</p>
 </div>
 <button
 type="button"
 onClick={() => navigate('/admin/payments')}
 className="text-sm text-ink hover:text-ink font-medium inline-flex items-center gap-1 shrink-0"
 >
 View all
 <ArrowUpRight className="w-4 h-4" />
 </button>
 </div>

 {visiblePayments.length > 0 ? (
 <div className="divide-y divide-gray-100">
 {visiblePayments.slice(0, 5).map((payment) => {
 const isPrivate = isPrivateBooking(payment);
 return (
 <div
 key={payment.id}
 role="button"
 tabIndex={0}
 onClick={() => openPayment(payment)}
 onKeyDown={(e) => {
 if (e.key === 'Enter' || e.key === ' ') {
 e.preventDefault();
 openPayment(payment);
 }
 }}
 className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 hover:bg-surface-muted/80 cursor-pointer transition-colors"
 >
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1 flex-wrap">
 <p className="text-sm font-semibold text-ink tabular-nums">
 {formatCurrency(paymentCatalogAmount(payment))}
 </p>
 {isPrivate && <PrivateBookingBadge />}
 <span
 className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(payment.status)}`}
 >
 {payment.status}
 </span>
 </div>
 <p className="text-xs text-ink-tertiary">
 {payment.booking?.booking_reference || 'Payment'} · {formatDate(payment.created_at)}
 </p>
 {isPrivate && (
 <p className="text-[11px] text-amber-700 mt-0.5">
 {PRIVATE_PAYOUT_EXCLUDED_NOTE}
 </p>
 )}
 </div>
 <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" />
 </div>
 );
 })}
 </div>
 ) : (
 <div className="text-center py-14 px-6">
 <CreditCard className="w-9 h-9 text-gray-300 mx-auto mb-3" />
 <p className="text-sm font-medium text-ink">No recent payments</p>
 <p className="text-xs text-ink-tertiary mt-1">Completed charges will show here.</p>
 </div>
 )}
 </div>
 );
};

export default RecentPayments;
